import React, { useEffect, useState } from "react";
import {
  RubricSummary,
  listRubrics,
  setScenarioRubric,
} from "../services/rubricAdminService";

interface AssignRubricModalProps {
  scenarioId: string;
  scenarioTitle: string;
  currentRubricId: string | null;
  orgSlug: string;
  getToken: () => string | undefined;
  onClose: () => void;
  onSaved: (rubricId: string | null, rubricName: string | null) => void;
}

const AssignRubricModal: React.FC<AssignRubricModalProps> = ({
  scenarioId,
  scenarioTitle,
  currentRubricId,
  orgSlug,
  getToken,
  onClose,
  onSaved,
}) => {
  const [eligible, setEligible] = useState<RubricSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(currentRubricId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setEligible(await listRubrics(getToken(), scenarioId));
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load rubrics");
      } finally {
        setLoading(false);
      }
    })();
    // getToken is read at call time; only scenarioId should retrigger the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await setScenarioRubric(scenarioId, selected, getToken());
      const name = selected ? eligible.find((r) => r.id === selected)?.name ?? null : null;
      onSaved(selected, name);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-surface border border-outline-variant/30 rounded-xl p-6 w-[28rem] text-on-surface">
        <h2 className="text-lg font-bold mb-1">Assign rubric</h2>
        <p className="text-slate-400 text-sm mb-4">{scenarioTitle}</p>

        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : eligible.length === 0 ? (
          <div className="text-slate-400 text-sm">
            No rubrics are available for this scenario yet. Add this scenario to a
            rubric's allowed scenarios in the{" "}
            <a href={`/${orgSlug}/rubrics`} className="text-primary underline">
              Rubric editor
            </a>
            .
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="rubric"
                checked={selected === null}
                onChange={() => setSelected(null)}
              />
              None (rubric features disabled)
            </label>
            {eligible.map((r) => (
              <label key={r.id} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="rubric"
                  checked={selected === r.id}
                  onChange={() => setSelected(r.id)}
                />
                {r.name}
              </label>
            ))}
          </div>
        )}

        {error && <div className="text-error text-sm mt-3">{error}</div>}

        <div className="flex justify-end gap-3 mt-5">
          <button type="button" className="text-slate-300 text-sm px-3 py-2" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bg-primary text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
            disabled={saving || loading}
            onClick={save}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignRubricModal;
